import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../assets/css/login.css';
import Navbar from '../components/Navbar';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import OAuthComponents from '../components/OAuthComponents';
import { BackgroundDiv } from '../components/StyledComponents';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  Link,
} from '@mui/material';
import SignupFooter from '../components/SignupFooter';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../context/AuthContext';
import posthog from 'posthog-js';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errMsg, setErrMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const errorFromGoogle = queryParams.get('error');

    if (errorFromGoogle) {
      setErrMsg(decodeURIComponent(errorFromGoogle));

      // Remove error param from URL
      const cleanedUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanedUrl);
    }
  }, [location.search]);

  // Clear error message when typing in input fields
  const handleInputChange = (setter) => (e) => {
    setter(e.target.value);
    setErrMsg(''); // Clear error message when typing in the inputs
  };

  const { login } = useAuth();
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrMsg('');
    setSuccessMsg('');

    try {
      const res = await axios.post(`${api}/user/login`, {
        email: email.toLowerCase(),
        password,
      });

      setSuccessMsg('Login successful!');
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('islogin', 'true');
      login();
      posthog.identify(email.toLowerCase());
      posthog.capture('login_completed', { method: 'email' });
      navigate('/detail', { replace: true });
    } catch (err) {
      setErrMsg(err.response?.data?.msg || 'Login failed.');
      posthog.captureException(err, { extra: { context: 'login_completed' } });
    }
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (event) => {
    event.preventDefault();
  };

  return (
    <BackgroundDiv>
      <Navbar />
      <Box
        sx={{
          width: '100%',
          justifyContent: 'center',
          display: 'flex',
          alignItems: 'flex-start',
        }}
      >
        <Box
          sx={{
            padding: { xs: '0px 20px', sm: '0px 30px', md: '0px 40px' },
            width: '100%',
            maxWidth: '500px',
          }}
        >
          <Box ml={2}>
            <Typography
              sx={{
                my: { xs: 4, sm: 5, md: 6 },
                fontSize: { xs: '32px', sm: '40px', md: '50px' },
                fontWeight: '500',
              }}
            >
              Welcome back
            </Typography>

            <form onSubmit={handleSubmit}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  width: { xs: '100%', sm: '350px', md: '387px' },
                }}
              >
                <TextField
                  label="Email address"
                  variant="outlined"
                  value={email}
                  onChange={handleInputChange(setEmail)}
                  required
                  sx={{ height: '60px', marginBottom: '40px' }}
                />
                <TextField
                  label="Password"
                  variant="outlined"
                  placeholder=""
                  required
                  value={password}
                  onChange={handleInputChange(setPassword)}
                  type={showPassword ? 'text' : 'password'}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={
                              showPassword
                                ? 'hide the password'
                                : 'display the password'
                            }
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            onMouseUp={handleMouseUpPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                  sx={{ marginBottom: '34px' }}
                />
                {errMsg && <Box sx={{ color: 'red' }}>{errMsg}</Box>}
                {successMsg && <Box sx={{ color: 'green' }}>{successMsg}</Box>}

                <Link
                  sx={{
                    color: '#ccc',
                    fontSize: '14px',
                  }}
                  color="secondary"
                  href="/forget-password"
                >
                  Forget password?
                </Link>

                <Button
                  variant="contained"
                  color="secondary"
                  type="submit"
                  sx={{
                    height: { xs: '50px', sm: '54px', md: '58px' },
                    fontSize: { xs: '18px', sm: '20px', md: '24px' },
                    fontStyle: 'Inter, sans-serif',
                    marginTop: { xs: '30px', sm: '35px', md: '40px' },
                  }}
                >
                  Continue
                </Button>
                <Typography
                  variant="body1"
                  textAlign="left"
                  sx={{ marginTop: '30px' }}
                >
                  Don't have an account?{' '}
                  <Link
                    href="/signin"
                    color="secondary"
                    sx={{
                      color: 'orange',
                      textDecoration: 'none',
                    }}
                  >
                    Sign up
                  </Link>
                </Typography>
              </Box>
            </form>
          </Box>

          <Typography
            variant="body1"
            textAlign="center"
            sx={{ marginTop: '30px', marginBottom: '30px' }}
          >
            OR
          </Typography>

          <Box mb={4}>
            <OAuthComponents />
          </Box>

          <SignupFooter />
        </Box>
      </Box>
    </BackgroundDiv>
  );
}
